/**
 * Email-first sign-up — three short steps, both web portals (design approved
 * 26 Sep 2026).
 *
 *   email ──► details (first name, last name, a password) ──► code ──► signed in
 *
 * The sign-up is created with the email alone (Clerk's progressive sign-up),
 * so an address that already has an account is said at step 1 — before the
 * person types a name and a password for nothing. Step 2 adds the rest and
 * sends the 6-digit code; step 3 proves the address and opens the session.
 *
 * A session the device still holds makes `create` refuse with "signed in";
 * it is signed out and the sign-up starts once more (`retryAfterStaleSession`).
 *
 * Nothing throws and nothing is put into words here: each call resolves to an
 * outcome, and a failure carries Clerk's error for `useAuthErrorMessage`.
 */

import { useClerk, useSignUp } from '@clerk/clerk-react';
import { useCallback, useState } from 'react';

import { retryAfterStaleSession } from './stale-session';

export type SignUpStep =
  { step: 'email' } | { step: 'details'; email: string } | { step: 'code'; email: string };

export type SignUpOutcome =
  | { outcome: 'next' }
  /** Signed up and in: the session is active. */
  | { outcome: 'complete' }
  | { outcome: 'failed'; error: unknown };

export interface SignUpDetails {
  firstName: string;
  lastName: string;
  password: string;
}

/** Clerk asked for something this app does not collect. */
export class SignUpFlowError extends Error {
  constructor(readonly code: string) {
    super(`Sign-up cannot continue: ${code}`);
    this.name = 'SignUpFlowError';
  }
}

const fail = (error: unknown): SignUpOutcome => ({ outcome: 'failed', error });

export function useEmailFirstSignUp() {
  const { signUp, isLoaded, setActive } = useSignUp();
  const clerk = useClerk();
  const [state, setState] = useState<SignUpStep>({ step: 'email' });

  /** Step 1: the address. An existing account is refused here (`form_identifier_exists`). */
  const start = useCallback(
    async (email: string): Promise<SignUpOutcome> => {
      if (!isLoaded || !signUp) return fail(new SignUpFlowError('not_loaded'));
      try {
        await retryAfterStaleSession(
          () => signUp.create({ emailAddress: email }),
          () => clerk.signOut()
        );
        setState({ step: 'details', email });
        return { outcome: 'next' };
      } catch (error) {
        return fail(error);
      }
    },
    [isLoaded, signUp, clerk]
  );

  /** Step 2: who they are and their password — then the code goes out. */
  const submitDetails = useCallback(
    async (details: SignUpDetails): Promise<SignUpOutcome> => {
      if (!signUp || state.step !== 'details') return fail(new SignUpFlowError('not_on_details'));
      try {
        const updated = await signUp.update(details);
        if (updated.status === 'complete') {
          await setActive?.({ session: updated.createdSessionId });
          return { outcome: 'complete' };
        }
        const missing = updated.missingFields ?? [];
        if (missing.length > 0) return fail(new SignUpFlowError(`missing_${missing.join('_')}`));
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        setState({ step: 'code', email: state.email });
        return { outcome: 'next' };
      } catch (error) {
        return fail(error);
      }
    },
    [signUp, state, setActive]
  );

  /** Step 3: the code proves the address; the account is made and signed in. */
  const verify = useCallback(
    async (code: string): Promise<SignUpOutcome> => {
      if (!signUp || state.step !== 'code') return fail(new SignUpFlowError('not_on_code'));
      try {
        const result = await signUp.attemptEmailAddressVerification({ code });
        if (result.status !== 'complete') {
          return fail(new SignUpFlowError(`status_${result.status}`));
        }
        await setActive?.({ session: result.createdSessionId });
        return { outcome: 'complete' };
      } catch (error) {
        return fail(error);
      }
    },
    [signUp, state, setActive]
  );

  const resendCode = useCallback(async (): Promise<SignUpOutcome> => {
    if (!signUp || state.step !== 'code') return fail(new SignUpFlowError('not_on_code'));
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      return { outcome: 'next' };
    } catch (error) {
      return fail(error);
    }
  }, [signUp, state]);

  /** One step back: from the code to the details, from the details to the email. */
  const back = useCallback(() => {
    setState((current) =>
      current.step === 'code'
        ? { step: 'details', email: current.email }
        : current.step === 'details'
          ? { step: 'email' }
          : current
    );
  }, []);

  return {
    state,
    ready: isLoaded && !!signUp,
    start,
    submitDetails,
    verify,
    resendCode,
    back,
  };
}
